import { useMemo } from "react";
import { api, CompanyEmployeeRoleType, CompanyRolePermission } from "shared/api";

export interface Permissions {
  finances: {
    budgets: {
      read: boolean;
      write: boolean;
      approve: boolean;
    };
    plannings: {
      read: boolean;
      write: boolean;
      approve: boolean;
    };

    fundingRequests: {
      create: boolean;
      readAll: boolean;
      acceptOrDecline: boolean;
      approve: boolean;
      pay: boolean;
      close: boolean;
    };

    funds: {
      withdraw: boolean;
      deposit: boolean;

      readProtected: boolean;
    };
  };
  company: {
    general: {
      update: boolean;
      delete: boolean;
    };

    members: {
      invite: boolean;
      update: boolean;
      remove: boolean;
    };

    roles: {
      write: boolean;
    };
  };
}

export const convertPermissions = (
  roleType: CompanyEmployeeRoleType,
  permissions: CompanyRolePermission[]
): Permissions => {
  const set = new Set(permissions);

  const isOwner = roleType === CompanyEmployeeRoleType.OWNER;

  return {
    finances: {
      budgets: {
        read: set.has(CompanyRolePermission.FINANCIAL_PLAN_READ),
        write: set.has(CompanyRolePermission.FINANCIAL_PLAN_CONDUCT),
        approve: set.has(CompanyRolePermission.FINANCIAL_PLAN_APPROVE),
      },
      plannings: {
        read: set.has(CompanyRolePermission.FINANCIAL_PLAN_READ),
        write: set.has(CompanyRolePermission.FINANCIAL_PLAN_CONDUCT),
        approve: set.has(CompanyRolePermission.FINANCIAL_PLAN_APPROVE),
      },
      fundingRequests: {
        create: true,
        readAll: set.has(CompanyRolePermission.CLAIM_READ_ALL),
        acceptOrDecline: set.has(CompanyRolePermission.CLAIM_ALLOW_FOR_FINANCIAL_PLANNING),
        approve: set.has(CompanyRolePermission.CLAIM_APPROVE),
        close: set.has(CompanyRolePermission.CLAIM_APPROVE) || set.has(CompanyRolePermission.CLAIM_PAY_OFF),
        pay: set.has(CompanyRolePermission.CLAIM_PAY_OFF),
      },
      funds: {
        withdraw: set.has(CompanyRolePermission.FUND_WITHDRAW),
        deposit: set.has(CompanyRolePermission.FUND_DEPOSIT),
        readProtected: set.has(CompanyRolePermission.FUND_SECURED_READ),
      },
    },
    company: {
      general: {
        update: isOwner,
        delete: isOwner,
      },
      members: {
        invite: isOwner,
        update: isOwner,
        remove: isOwner,
      },
      roles: {
        write: isOwner,
      },
    },
  };
};

export const useMyPermissions = (companySlug: string): Permissions | null => {
  const me = api.companyEmployee.useCompanyEmployeeMe(companySlug);
  const role = me.data?.companyEmployeeRole;

  return useMemo(() => (!role ? null : convertPermissions(role.type, role.permissions)), [role]);
};
